import { memo } from 'react'
import { CallBackProps, Step } from 'react-joyride'
import { useI18n } from 'builder/hooks/useI18n'
import { JoyRide } from 'builder/components/JoyRide'

import { Pulse } from './Pulse/Pulse'
import { Tooltip } from './Tooltip/Tooltip'
import { SkipButton } from './SkipButton/SkipButton'

import { useTourConfig } from './hooks/useTourConfig'
import { useDownloadSteps } from './hooks/useDownloadSteps'
import { useDesignTourSteps } from './hooks/useDesignTourSteps'
import { useEditorTourSteps } from './hooks/useEditorTourSteps'

import { EditorTourStepIndex, StepName } from './types'
import { overlayClickCallback, TRANSLATION_KEY, Z_INDEX } from './utils'

export const DocumentTour = memo(() => {
  const { i18n } = useI18n()
  const downloadSteps = useDownloadSteps()
  const editorSteps = useEditorTourSteps()
  const designTourSteps = useDesignTourSteps()
  const { stepName, stepIndex, showSkipButton, isTourRunning, skipAll } = useTourConfig()

  const steps: Record<StepName, Step[]> = {
    editor: editorSteps,
    design: designTourSteps,
    download: downloadSteps,
  }
  const activeSteps = steps[stepName || 'design']
  const isEditorStep = stepName === 'editor'
  const isDownloadStep = stepName === 'download'
  const isDragSectionsStep = isEditorStep && stepIndex === EditorTourStepIndex.DRAG_SECTIONS

  const onCallback = (data: CallBackProps) => {
    if (data.action === 'start') {
      overlayClickCallback()
    }
  }

  return (
    <>
      {!isDownloadStep && showSkipButton && (
        <SkipButton label={i18n.t(`${TRANSLATION_KEY}.skip_all`)} onSkipAll={skipAll} />
      )}
      <JoyRide
        run={isTourRunning}
        steps={activeSteps}
        scrollOffset={200}
        callback={onCallback}
        scrollDuration={1000}
        showSkipButton={false}
        disableScrollParentFix={true}
        stepIndex={stepIndex}
        styles={{ options: { zIndex: Z_INDEX, overlayColor: 'rgba(0, 0, 0, 0.2)' } }}
        customToolTip={props => <Tooltip {...props} stepName={stepName || 'design'} />}
      />
      {isTourRunning && (
        <Pulse disablePulse={isDragSectionsStep} step={activeSteps[stepIndex ?? 0]} />
      )}
    </>
  )
})
